import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Spinner, Alert, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useCustomerRewardPoints } from 'modules/transactions/new-sales-entry/services/use-sales-entry.service';
import { useSalesEntry } from 'contexts/SalesEntryContext';
import { calculationService } from 'services/calculationService';
import { apiClient } from 'helpers/api_helper';

interface RewardPointsModalProps {
  show: boolean;
  onHide: () => void;
  currentRewardPoints: number;
  onRewardPointsSubmit: (points: number, distribution: any) => void;
  customerId?: string;
}

interface FormErrors {
  [materialTypeId: number]: string | undefined;
}

export const RewardPointsModal: React.FC<RewardPointsModalProps> = ({
  show,
  onHide,
  currentRewardPoints,
  onRewardPointsSubmit,
  customerId
}) => {
  const { state, actions } = useSalesEntry();
  const [rewardPointsBalance, setRewardPointsBalance] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [noPointsMessage, setNoPointsMessage] = useState<string>('');
  const [maxCanReedemPoint, setMaxCanReedemPoint] = useState<number>(0);
  const [materialRedeemPoints, setMaterialRedeemPoints] = useState<{ [key: number]: number }>({});
  const [errors, setErrors] = useState<FormErrors>({});
  const [maxPointsPerType, setMaxPointsPerType] = useState<{ [key: number]: number }>({});

  const { getCustomerAvailableRewardPoints } = useCustomerRewardPoints();

  const calculateNetPriceForRewardPoints = (item: any) => {
    const calculationInput = {
      items: [item],
      discounts: {
        consumerOfferTotal: item.totalConsumerDiscount || 0,
        couponDiscount: 0,
        storeDiscount: 0,
        membershipDiscount: 0,
        coupon: null
      }
    };

    try {
      const result = calculationService.calculateSalesEntry(calculationInput);

      return result.summary.taxableAmount + result.summary.totalTax;
    } catch (error) {
      console.error('Error calculating net price:', error);
      return (item.mrpPrice || 0) * 1.03 - (item.totalConsumerDiscount || 0);
    }
  };

  useEffect(() => {
    if (state.customer?.rewardPoints && state.items.length > 0) {
      const aggregatedData = state.items.reduce(
        (acc, item) => {
          const matchedType = state.materialTypes?.find(
            (type) => type.materialTypeId === Number(item.materialTypeId)
          );
          if (matchedType) {
            const existing = acc.find((x) => x.materialTypeId === matchedType.materialTypeId);
            const netPrice = calculateNetPriceForRewardPoints(item);

            if (existing) {
              existing.totalNetPrice += netPrice;
            } else {
              acc.push({
                materialTypeId: matchedType.materialTypeId,
                materialTypeName: matchedType.materialTypeName,
                perRewardPointRs: matchedType.perRewardPointRs,
                maxPercentageOnInvoice: matchedType.maxPercentageOnInvoice,
                totalNetPrice: netPrice,
              });
            }
          }
          return acc;
        },
        [] as {
          materialTypeId: number;
          materialTypeName: string;
          perRewardPointRs: number;
          maxPercentageOnInvoice: number;
          totalNetPrice: number;
        }[]
      );

      let typeWise: { [key: number]: number } = {};
      const redeemableData = aggregatedData.map((row) => {
        const maxRedeemAmount = (row.totalNetPrice * row.maxPercentageOnInvoice) / 100;
        const maxRedeemPoints = maxRedeemAmount / row.perRewardPointRs;
        typeWise[row.materialTypeId] = Number(Math.round(maxRedeemPoints));
        return Number(Math.round(maxRedeemPoints));
      });

      setMaxPointsPerType(typeWise);

      const totalMaxRedeemPoints = redeemableData.reduce((sum, pts) => sum + pts, 0);
      const finalAllowedPoints = Math.min(totalMaxRedeemPoints, state.customer.rewardPoints);
      setMaxCanReedemPoint(Number(Math.round(finalAllowedPoints)));
    }
  }, [state.items, show, state.materialTypes]);

  useEffect(() => {
    const fetchRewardPoints = async () => {
      if (!customerId) {
        setMaterialRedeemPoints({});
        setRewardPointsBalance(0)
        setMaxCanReedemPoint(0)
        return;
      }
      if (!show) return;

      setLoading(true);
      setError('');
      setNoPointsMessage('');

      try {
        const response = await getCustomerAvailableRewardPoints(customerId);
        const res = await apiClient.get('/api/v1/Reward/GetAllActiveRedemptionSettings');
        if (res.data) {
          actions.setMaterialTypes(res.data);
        }
        const points =
          response?.availablePoints && !isNaN(Number(response.availablePoints))
            ? Number(response.availablePoints)
            : 0;

        if (state.customer) {
          actions.setCustomer({
            ...state.customer,
            rewardPoints: points,
          });
          
          setRewardPointsBalance(points);
        }
        if (points === 0) {
          setNoPointsMessage('No reward points');
        }
      } catch (err: any) {
        setError(err.message);
        toast.error(err.message);
        setRewardPointsBalance(0);
      } finally {
        setLoading(false);
      }
    };

    fetchRewardPoints();
    setErrors({});
    setMaterialRedeemPoints({});
  }, [show, customerId, currentRewardPoints]);

  const materialTypes = state.materialTypes;
  const items = state.items;

  const rewardData = materialTypes?.filter(mt =>
    items.some(it => it.materialTypeName === mt.materialTypeName)
  ) || [];

  const handleRedeemChange = (matId: number, value: number) => {
    const totalEntered = Object.values({ ...materialRedeemPoints, [matId]: value })
      .reduce((sum, v) => sum + (Number(v) || 0), 0);

    if (totalEntered > maxCanReedemPoint) {
      toast.error(`Total cannot exceed ${maxCanReedemPoint}`);
      return;
    }

    setMaterialRedeemPoints(prev => ({
      ...prev,
      [matId]: value
    }));

    setErrors(prev => ({
      ...prev,
      [matId]: value <= 0 ? "Enter positive points" : undefined
    }));
  };

  const handleSubmit = () => {
    const totalEntered = Object.values(materialRedeemPoints)
      .reduce((sum, v) => sum + (Number(v) || 0), 0);

    if (totalEntered === 0) {
      toast.error("Please enter redeem points");
      return;
    }
    if (totalEntered > maxCanReedemPoint) {
      toast.error(`Total cannot exceed ${maxCanReedemPoint}`);
      return;
    }

    actions.removeRewardPoints();
    actions.clearRewardRedeemDetails();

    const offers = state?.consumerOfferDetail || [];
    const realOffers = offers.filter(offer =>
      (offer.productId && !offer.productId.startsWith('test_') &&
        state?.items.some(item => item.id === offer.productId))
      || !offer.productId
    );
    const selectedOffers = realOffers.filter(offer => offer.isSelected);

    const hasClubMaterialSelected = Object.keys(materialRedeemPoints).some(matIdStr => {
      const matId = Number(matIdStr);
      const matType = materialTypes?.find(mt => mt.materialTypeId === matId);
      return matType?.isClub;
    });

    if (selectedOffers.length > 0 && !hasClubMaterialSelected) {
      toast.warn("Cannot redeem reward points for clubbed materials with active consumer offers.");
      return;
    }

    const distribution: {
      [matId: number]: {
        materialTypeName: string;
        items: { [itemId: number]: number };
      };
    } = {};

    Object.entries(materialRedeemPoints).forEach(([matIdStr, points]) => {
      const matId = Number(matIdStr);
      const itemsOfType = items.filter(it => Number(it.materialTypeId) === matId);

      const totalNetPrice = itemsOfType.reduce((sum, it) => sum + calculateNetPriceForRewardPoints(it), 0);

      itemsOfType.forEach((it: any) => {
        const netPrice = calculateNetPriceForRewardPoints(it);
        const share = totalNetPrice > 0 ? (netPrice / totalNetPrice) * points : 0;
        const matType = materialTypes?.find(mt => mt.materialTypeId === matId);
        const perRewardPointRs = matType?.perRewardPointRs || 1;
        if (!distribution[matId]) {
          distribution[matId] = {
            materialTypeName: it.materialTypeName,
            items: {}
          };
        }
        distribution[matId].items[it.id] = (share * perRewardPointRs);
      });
    });

    actions.applyRewardPoints(distribution);
    onRewardPointsSubmit(totalEntered, distribution);
    const grandTotalAmt = Object.values(distribution)
      .flatMap(dist => Object.values(dist.items))
      .reduce((a, b) => a + b, 0);
    actions.setRewardRedeemDetails(
      Object.entries(distribution).map(([matIdStr, dist], idx) => {
        const matId = Number(matIdStr);
        const matType = materialTypes?.find(mt => mt.materialTypeId === matId);
        return {
          id: idx,
          materialTypeId: matId,
          usedPoints: materialRedeemPoints[matId] || 0,
          amountConverted: Object.values(dist.items).reduce((a, b) => a + b, 0),
          totalAmt: grandTotalAmt,
          rewardPercentage: matType?.maxPercentageOnInvoice || 0,
          isClub: matType?.isClub,
        };
      })
    );
    handleClose();
  };

  const handleRemove = () => {
    onRewardPointsSubmit(0, {});
    actions.removeRewardPoints()
    actions.clearRewardRedeemDetails();
    handleClose();
  };

  const handleClose = () => {
    setMaterialRedeemPoints({});
    setErrors({});
    onHide();
  };

  useEffect(() => {
    if (show) {
      setErrors({});
      if (state.rewardRedeemDetailsModelsList?.length) {
        const prevPoints: { [key: number]: number } = {};
        state.rewardRedeemDetailsModelsList.forEach(detail => {
          prevPoints[detail.materialTypeId] = detail.usedPoints;
        });
        setMaterialRedeemPoints(prevPoints);
      } else {
        setMaterialRedeemPoints({});
      }
    }
  }, [show, state.rewardRedeemDetailsModelsList]);

  return (
    <Modal show={show} onHide={handleClose} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="text-secondary fw-normal">
          Reward Points
          {/* <Badge bg="info" className="ms-2">
            Max: {maxCanReedemPoint}
          </Badge> */}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading && (
          <div className="text-center mb-3">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading reward points...
          </div>
        )}

        {error && (
          <Alert variant="danger" className="mb-3">
            <i className="bx bx-error-circle me-2"></i>
            {error}
          </Alert>
        )}

        {noPointsMessage && !error && (
          <Alert variant="info" className="mb-3">
            <i className="bx bx-info-circle me-2"></i>
            {noPointsMessage}
          </Alert>
        )}

        <Form>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="text-primary fw-semibold">
                  Reward Points Balance
                </Form.Label>
                <Form.Control
                  type="text"
                  value={rewardPointsBalance.toLocaleString()}
                  readOnly
                  className="bg-light text-center fw-bold"
                  style={{ fontSize: '16px' }}
                />
              </Form.Group>
            </Col>
          </Row>

          {rewardPointsBalance > 0 ? rewardData.map((row) => (
            <Row className="mb-3" key={row.materialTypeId}>
              <Col md={8}>
                <Form.Group>
                  <Form.Label className="fw-semibold d-flex justify-content-between align-items-center">
                    <span>
                      {row.materialTypeName} Points
                      <span className="text-muted ms-2">
                        (Max: {maxPointsPerType[row.materialTypeId] || 0})
                      </span>
                    </span>
                    <span className="text-primary small">
                      1 Point = ₹{row.perRewardPointRs}
                    </span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    max={maxPointsPerType[row.materialTypeId]}
                    value={materialRedeemPoints[row.materialTypeId] || ""}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      const maxVal = maxPointsPerType[row.materialTypeId];
                      handleRedeemChange(
                        row.materialTypeId,
                        val > maxVal ? maxVal : val < 0 ? 0 : val
                      );
                    }
                    }
                    isInvalid={!!errors[row.materialTypeId]}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors[row.materialTypeId]}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          ))
            : null
          }
        </Form>
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <div>
          {currentRewardPoints > 0 && (
            <Button variant="warning" className="me-2" onClick={handleRemove}>
              Remove
            </Button>
          )}
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!rewardPointsBalance}
            style={{ backgroundColor: '#6366f1', borderColor: '#6366f1' }}
          >
            Confirm
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};